import { useTranslation } from 'react-i18next';

import { checkModuleRight, useModuleRights } from '@work4all/data';
import { useEntityEvents } from '@work4all/data/lib/entity-events/use-entity-events';

import { ReViewModel } from '@work4all/models/lib/Classes/ReViewModel.entity';
import { SortDirection } from '@work4all/models/lib/DataProvider';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';
import { ModuleAccessRightType } from '@work4all/models/lib/Enums/ModuleAccessRightType.enum';
import { PaymentStatus } from '@work4all/models/lib/Enums/PaymentStatus.enum';

import { usePaymentMask } from '../../../hooks/use-payment-mask';
import { useBottomViews } from '../../bottom-views';
import { DueDateClassifiedCell } from '../DueDateClassifiedCell';
import { DueDaysCell } from '../DueDaysCell';
import { EntityTableV8, IEntityTableV8 } from '../entity-table/EntityTableV8';
import { useEntityDataTableV8 } from '../entity-table/use-entity-data-table-v8';
import { InvoiceKindCell } from '../InvoiceKindCell';
import { PaymentStatusCell } from '../PaymentStatusCell';
import { PermitStatusCell } from '../PermitStatusCell';
import { ReAccountingStatusCell } from '../ReAccountingStatusCell';
import schema from '../schemata/reViewModel-table-schema.json';
import { useDeleteEntitiesToolbarConfig } from '../use-delete-entities-toolbar-config';
import { useErpHandlersTemplateV8 } from '../use-erp-handlers-template-v8';

const defaultSort = [
  { field: 'invoiceDate', direction: SortDirection.DESCENDING },
];

const secondarySort = [
  { field: 'invoiceNumber', direction: SortDirection.DESCENDING },
];

const DELETABLE_STATUS = [
  PaymentStatus.ZERO_INVOICE,
  PaymentStatus.OPEN_CREDIT,
  PaymentStatus.OPEN,
  PaymentStatus.DUE,
];

const forceRequestFields: ReViewModel = {
  invoiceNumber: null,
  datevDate: null,
};
export function ReTable(props: IEntityTableV8) {
  const { rights } = useModuleRights();

  const incomingInvoiceLedgerModule = checkModuleRight(
    rights,
    ModuleAccessRightType.FEATURE_INCOMING_INVOICE_LEDGER
  );

  const dataTable = useEntityDataTableV8<ReViewModel, never>({
    ...props,
    schema: schema as never,
    defaultSort,
    secondarySort,
    enableFooter: true,
    cells: {
      PaymentStatus: PaymentStatusCell,
      ReAccountingStatus: ReAccountingStatusCell,
      PermitStatus: PermitStatusCell,
      InvoiceKind: InvoiceKindCell,
      DueDateClassified: DueDateClassifiedCell,
      DueDays: DueDaysCell,
    },
    forceRequestFields,
    skip: !incomingInvoiceLedgerModule,
  });

  const template = useErpHandlersTemplateV8(
    dataTable.table,
    'supplier.id',
    Entities.supplier
  );

  const { t } = useTranslation();

  const openPaymentMaskAction = usePaymentMask(
    Entities.reViewModel,
    dataTable.refetch
  );

  const deleteConfig = useDeleteEntitiesToolbarConfig({
    entityType: Entities.inboundInvoice,
    selectedEntities: dataTable.selectedEntities,
  });

  const prefilter = [{ id: { $eq: dataTable.selectedEntity?.id } }];
  const bottomArea = useBottomViews({
    skip: !dataTable.selectedEntity,
    views: [
      {
        type: 'table',
        entity: Entities.rELedgerAccountSplit,
        titleTranslationKey: 'COMMON.BOOKINGS',
        options: {
          prefilter,
        },
      },
      {
        type: 'table',
        entity: Entities.inboundInvoicePayment,
        titleTranslationKey: 'COMMON.PAYMENTS',
        options: {
          prefilter,
        },
      },
    ],
  });

  useEntityEvents((event) => {
    if (event.type === 'delete' && event.entity === Entities.inboundInvoice) {
      dataTable.refetch();
    }
  });

  return (
    <EntityTableV8
      {...props}
      {...dataTable}
      displayFooter
      template={template}
      areas={{
        bottom: bottomArea,
      }}
      actions={{
        custom: { left: [openPaymentMaskAction] },
        remove: {
          ...deleteConfig,
          canDeleteEntity: (id) => {
            const entity = dataTable.selectedEntities.find(
              (item) => item.id === Number(id)
            );
            const canDelete =
              DELETABLE_STATUS.includes(entity.paymentStatus) &&
              !entity.datevDate;

            const preventMessage = !DELETABLE_STATUS.includes(
              entity.paymentStatus
            )
              ? t('ALERTS.OUTGOING_INVOICE_PAYMENTS_EXIST', {
                  number: entity?.invoiceNumber,
                })
              : entity.datevDate
              ? t('ALERTS.OUTGOING_INVOICE_HANDOVER', {
                  number: entity?.invoiceNumber,
                })
              : null;

            return {
              value: canDelete,
              preventMessage: preventMessage,
            };
          },
        },
      }}
    />
  );
}
